import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import {
  Calendar,
  Search,
  AlertTriangle,
  ArrowRight,
  Droplets,
  ThermometerSun,
  Wind,
  Home,
} from "lucide-react";

export default function SignsYouNeedRoofReplacement() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id":
            "https://www.saskroofingcompany.com/blog/signs-you-need-roof-replacement#article",
          headline: "9 Signs Your Saskatchewan Home Needs a Roof Replacement",
          description:
            "Most roofs give years of warning before leaking. Learn the 9 critical signs that it's time to replace your roof in Saskatchewan.",
          author: { "@id": "https://www.saskroofingcompany.com/#organization" },
          publisher: {
            "@id": "https://www.saskroofingcompany.com/#organization",
          },
          datePublished: "2026-06-15",
          inLanguage: "en-CA",
          isPartOf: {
            "@id": "https://www.saskroofingcompany.com/blog#webpage",
          },
          mainEntityOfPage:
            "https://www.saskroofingcompany.com/blog/signs-you-need-roof-replacement",
          url: "https://www.saskroofingcompany.com/blog/signs-you-need-roof-replacement",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.saskroofingcompany.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.saskroofingcompany.com/blog",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Signs You Need Roof Replacement",
              item: "https://www.saskroofingcompany.com/blog/signs-you-need-roof-replacement",
            },
          ],
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How do I know if I need a new roof or just a repair?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "If the damage is isolated to a few shingles or one flashing area, a repair is usually sufficient. If the shingles are brittle, losing granules across the entire roof, or the roof is over 20 years old, replacement is typically the better investment.",
          },
        },
        {
          "@type": "Question",
          name: "How long does an asphalt shingle roof last in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "In Saskatchewan's extreme climate, a standard asphalt shingle roof typically lasts 20 to 25 years. Premium architectural shingles may last slightly longer if properly ventilated.",
          },
        },
        {
          "@type": "Question",
          name: "What are the most common signs of roof damage?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The most common signs include curling or cracked shingles, heavy granule loss in the gutters, water stains on interior ceilings, and visible sagging on the roof deck.",
          },
        },
        {
          "@type": "Question",
          name: "Can I just patch a leaking roof instead of replacing it?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, if the roof is relatively young and the leak is due to isolated damage (like a blown-off shingle or failed flashing). However, patching an old, brittle roof is often a temporary fix that wastes money in the long run.",
          },
        },
        {
          "@type": "Question",
          name: "How do I know how old my roof is?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "You can check your home purchase documents, look for building permits on file with your city, or have a professional roofer inspect it. An experienced roofer can estimate the age based on the condition of the asphalt and granules.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="9 Signs You Need a Roof Replacement | Sask Roofing"
        description="Most roofs give years of warning before leaking. Learn the 9 critical signs that it's time to replace your roof in Saskatchewan."
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/c465427c-c55b-4937-bd09-e8ebb5af446a.png&output=webp&q=60"
            alt="Close-up of curling and bald asphalt shingles indicating roof age"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-4xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/80 text-primary-foreground text-sm font-medium mb-4">
              <Calendar className="w-4 h-4" /> Published June 15, 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              9 Signs Your Saskatchewan Home Needs a Roof Replacement
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Most roofs give years of warning before they start leaking into
              your living room. Knowing the signs can save you from emergency
              calls and extensive interior water damage.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Intro */}
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                A{" "}
                <Link
                  to="/services/replacement"
                  className="text-primary font-medium hover:underline"
                >
                  roof replacement
                </Link>{" "}
                is a significant investment, and it's natural to want to put it
                off as long as possible. However, waiting until water is
                dripping onto your dining room table turns a planned exterior
                project into an emergency interior renovation.
              </p>
              <p>
                The good news? Your roof will almost always tell you when it's
                reaching the end of its life. In Saskatchewan's harsh
                climate—with our intense UV summers, brutal deep-freezes, and
                sudden hailstorms—roofs age differently than they do in milder
                regions. Here are the nine clearest signs that your home is due
                for a new roof.
              </p>
            </div>

            {/* TL;DR Box */}
            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-xl shadow-sm">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground mb-3">
                <Search className="w-6 h-6 text-primary" />
                The Short Answer
              </h2>
              <p className="text-lg text-muted-foreground mb-0">
                If your asphalt roof is over 20 years old, has curling or
                missing shingles, heavy granule loss, or if you're seeing water
                stains on your interior ceilings, it is time to call a
                professional. Don't wait for a catastrophic leak to take action.
              </p>
            </div>

            {/* The 9 Signs */}
            <div className="space-y-12">
              <h2 className="text-3xl font-bold text-foreground">
                The 9 Warning Signs
              </h2>

              {/* Sign 1 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  01
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Roof Age</h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      In Saskatchewan, a standard asphalt shingle roof typically
                      lasts 20 to 25 years. Metal roofs can last 40 to 50 years
                      or more. If your roof is approaching these ages without a
                      recent replacement, it's time to start planning and
                      budgeting.
                    </p>
                    <p>
                      Age in itself isn't an immediate death sentence for your
                      roof, but it is the primary indicator that materials are
                      becoming brittle and less effective at shedding water. If
                      you live in an older neighborhood and notice all your
                      neighbors are getting new roofs, yours is likely due as
                      well.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sign 2 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  02
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    Curling, Cracking, or Missing Shingles
                  </h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      Shingles are designed to lie flat against the roof deck.
                      When they reach the end of their lifespan, the edges will
                      begin to curl upward (cupping) or the middle will come up
                      (clawing).
                    </p>
                    <p>
                      You may also see visible cracks across the shingle
                      surface, or notice entire shingles missing after a
                      windstorm. These are signs that the asphalt has dried out
                      and the sealant strips have failed. These issues are often
                      visible from the ground—a quick look with binoculars can
                      tell you a lot.
                    </p>
                  </div>
                </div>
              </div>

              {/* Inline Photo 1 */}
              <div className="my-10">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/ff37d77b-d887-4f64-9492-693b3247af5b.png&output=webp&q=60"
                  alt="Damaged roof showing curled shingles and ice damming issues"
                  className="w-full rounded-xl shadow-md"
                />
                <p className="text-sm text-muted-foreground text-center mt-3">
                  Curled shingles and ice build-up are clear indicators of a
                  failing roofing system.
                </p>
              </div>

              {/* Sign 3 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  03
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    Granules in the Gutters
                  </h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      The rough, sand-like granules on your shingles aren't just
                      for texture—they protect the asphalt coating from the
                      sun's damaging UV rays and provide fire resistance.
                    </p>
                    <p>
                      As shingles age, they lose these granules. If you look at
                      your roof and see smooth, dark "bald spots," or if you
                      clean your gutters and find them filled with heavy, coarse
                      black sand, your roof's primary defense is failing. Once
                      the granules are gone, the asphalt degrades rapidly.
                    </p>
                  </div>
                </div>
              </div>

              {/* Callout 1 */}
              <div className="bg-accent/10 border border-accent/20 p-6 rounded-xl my-8">
                <h4 className="flex items-center gap-2 text-lg font-bold text-foreground mb-2">
                  <Droplets className="w-5 h-5 text-accent" />
                  The Granule Test
                </h4>
                <p className="text-muted-foreground mb-0">
                  Check your downspout splash blocks after a heavy rain. If you
                  see a thick layer of granules washed out onto your driveway or
                  lawn, your shingles are deteriorating fast.
                </p>
              </div>

              {/* Sign 4 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  04
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Sagging Roof Deck</h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      A healthy roof should have straight, crisp lines. If you
                      step back from your house and notice a visible sag or dip
                      between the rafters, you have a serious structural
                      concern.
                    </p>
                    <p>
                      Sagging typically indicates that moisture has penetrated
                      the shingles and rotted the plywood decking underneath, or
                      that the structural supports are failing under snow load.
                      This isn't a "wait until next year" problem—call a
                      professional within days, not weeks.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sign 5 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  05
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    Daylight Through Roof Boards
                  </h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      One of the easiest ways to check your roof's health is
                      from the inside. Go into your attic on a bright, sunny day
                      and turn off the lights.
                    </p>
                    <p>
                      Look up at the roof deck. While you should see light
                      coming through your designated roof vents, you should
                      absolutely <strong>not</strong> see light coming through
                      the plywood boards or along the rafters. Every pinprick of
                      daylight is a hole where water can—and will—enter your
                      home.
                    </p>
                  </div>
                </div>
              </div>

              {/* Inline Photo 2 */}
              <div className="my-10">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/1600982c-b626-48b2-8bd2-2a601866b6a7.png&output=webp&q=60"
                  alt="Tear-off process revealing damaged decking that needs replacement"
                  className="w-full rounded-xl shadow-md"
                />
                <p className="text-sm text-muted-foreground text-center mt-3">
                  During a replacement, we strip the roof down to the bare deck
                  to inspect for and replace rotted wood.
                </p>
              </div>

              {/* Sign 6 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  06
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    Ice Damming Damage
                  </h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      Ice damming occurs when heat escapes your attic, melts
                      snow on the roof, and refreezes at the colder eaves,
                      causing water to back up under the shingles.
                    </p>
                    <p>
                      If you experience repeated winter leaks from the same
                      area, or see massive icicles forming every year, it
                      indicates a combination of poor attic insulation,
                      inadequate ventilation, and compromised shingles. A proper
                      roof replacement will address the ventilation and add
                      critical ice & water shield membrane.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sign 7 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  07
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    Water Stains on Interior Ceilings
                  </h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      Brown or yellow ring-like stains on your interior ceilings
                      or running down your walls are the classic signs of an
                      active roof leak.
                    </p>
                    <p>
                      These often appear after a heavy rain or rapid snowmelt.
                      Because water can travel along rafters and pipes before
                      dropping onto your ceiling, the actual leak might be far
                      away from the stain. This usually indicates a slow leak
                      that has been saturating your insulation for months.
                    </p>
                  </div>
                </div>
              </div>

              {/* Callout 2 */}
              <div className="bg-destructive/10 border border-destructive/20 p-6 rounded-xl my-8">
                <h4 className="flex items-center gap-2 text-lg font-bold text-foreground mb-2">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  Repair vs. Replace
                </h4>
                <p className="text-muted-foreground mb-0">
                  If the water stain is caused by a single failed flashing
                  around a chimney, a{" "}
                  <Link
                    to="/services/repair"
                    className="text-primary hover:underline"
                  >
                    roof repair
                  </Link>{" "}
                  might fix it. If the stains are appearing in multiple rooms,
                  the entire roofing system is likely compromised.
                </p>
              </div>

              {/* Sign 8 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  08
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">
                    Sudden Spike in Heating Bills
                  </h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      A worn roof with compromised ventilation and wet
                      insulation loses its ability to regulate temperature.
                    </p>
                    <p>
                      If you notice a sudden, unexplained spike in your winter
                      heating bills or summer cooling costs, it means your HVAC
                      system is working overtime to compensate for heat loss
                      through the attic. This is often combined with visible
                      attic daylight or dampness.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sign 9 */}
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="text-5xl font-bold text-primary/20 shrink-0 mt-1">
                  09
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Storm Damage</h3>
                  <div className="prose prose-lg text-muted-foreground">
                    <p>
                      Saskatchewan is famous for its intense summer storms.
                      Hail, high winds, or a fallen tree branch can instantly
                      compromise a roof's life expectancy.
                    </p>
                    <p>
                      Even a single severe hailstorm can bruise shingles and
                      knock off years of protective granules. The good news is
                      that sudden storm damage is typically covered by your
                      homeowner's insurance. If your neighborhood just got hit,
                      call us first to document the damage for your adjuster.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* What to Do */}
            <div className="bg-muted p-8 rounded-2xl border mt-12">
              <h2 className="text-2xl font-bold mb-4">
                What to Do When You Notice These Signs
              </h2>
              <div className="prose prose-lg text-muted-foreground mb-6">
                <p>
                  First and foremost:{" "}
                  <strong>don't climb on the roof yourself.</strong> Old,
                  brittle roofs are incredibly slippery and dangerous to walk
                  on, and you risk causing further damage to the fragile
                  shingles.
                </p>
                <p>
                  Instead, call Sask Roofing Company for a{" "}
                  <Link
                    to="/services/inspections"
                    className="text-primary font-medium hover:underline"
                  >
                    free professional inspection
                  </Link>
                  . We will get up there, take detailed photos, and tell you
                  straight whether you need a full replacement or if a targeted
                  repair will buy you a few more years. If it's storm-related,
                  we'll document everything you need for your insurance claim.
                </p>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="pt-12 border-t">
              <h2 className="text-3xl font-bold mb-8">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="faq-1">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    How do I know if I need a new roof or just a repair?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    If the damage is isolated to a few shingles or one flashing
                    area, a repair is usually sufficient. If the shingles are
                    brittle, losing granules across the entire roof, or the roof
                    is over 20 years old, replacement is typically the better
                    investment.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-2">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    How long does an asphalt shingle roof last in Saskatchewan?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    In Saskatchewan's extreme climate, a standard asphalt
                    shingle roof typically lasts 20 to 25 years. Premium
                    architectural shingles may last slightly longer if properly
                    ventilated.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-3">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    What are the most common signs of roof damage?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    The most common signs include curling or cracked shingles,
                    heavy granule loss in the gutters, water stains on interior
                    ceilings, and visible sagging on the roof deck.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-4">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    Can I just patch a leaking roof instead of replacing it?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    Yes, if the roof is relatively young and the leak is due to
                    isolated damage (like a blown-off shingle or failed
                    flashing). However, patching an old, brittle roof is often a
                    temporary fix that wastes money in the long run.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-5">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    How do I know how old my roof is?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    You can check your home purchase documents, look for
                    building permits on file with your city, or have a
                    professional roofer inspect it. An experienced roofer can
                    estimate the age based on the condition of the asphalt and
                    granules.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">Ready to Find Out for Sure?</h2>
          <p className="text-xl text-primary-foreground/80">
            Schedule a free, no-obligation roof inspection. We'll tell you
            straight whether you need a full replacement or just a repair to buy
            some time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-md font-bold text-lg hover:bg-accent/90 transition-colors"
            >
              Book a Free Inspection <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
